import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AcademyHero } from '@/components/AcademyHero';
import { ProfilePhotoCard } from '@/components/ProfilePhotoCard';
import { RoleCards, ROLE_IMG } from '@/components/RoleCards';
import { Calendar, Radio, Mic, Inbox, Users, Volume2, Square } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useHdr = () => { const { token } = useAuth(); return { Authorization: `Bearer ${token}` }; };
const dfmt = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
export const Box = ({ children, testId }) => <div className="rvk-card p-5 flex flex-col gap-3" data-testid={testId}>{children}</div>;
export const Tag = ({ label, tone = 'ok' }) => (
  <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{
    background: tone === 'ok' ? '#E5F2EA' : tone === 'bad' ? '#F8E7E3' : '#F1DCE2',
    color: tone === 'ok' ? '#1F6B45' : tone === 'bad' ? '#9A3B2E' : '#7E5F2E' }}>{label}</span>
);

export default function EducatorHome() {
  const hdr = useHdr(); const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  useEffect(() => { axios.get(`${API}/educator/overview`, { headers: hdr }).then(r => setOv(r.data)).catch(() => {}); }, []); // eslint-disable-line
  return (
    <div className="space-y-8" data-testid="educator-home">
      <AcademyHero eyebrow={`Educator · ${ov ? ov.country.charAt(0).toUpperCase() + ov.country.slice(1) : ''} · ${(ov?.brands || []).join(' and ')}`}
        title="Ready to teach"
        lede={ov ? `${ov.upcoming} session${ov.upcoming !== 1 ? 's' : ''} coming up · ${ov.new_requests} new training request${ov.new_requests !== 1 ? 's' : ''}.` : ' '}
        testId="educator-achero" />
      {ov?.next_event && (
        <Box testId="educator-next-session">
          <p className="eyebrow" style={{ margin: 0 }}>Next session</p>
          <h2 className="text-xl">{ov.next_event.brand} · {ov.next_event.course}</h2>
          <p className="text-sm text-[#64525D]">{dfmt(ov.next_event.date)} at {ov.next_event.time} · {ov.next_event.venue}, {ov.next_event.city} · {ov.next_event.booked.length} of {ov.next_event.cap} booked</p>
          <div className="flex gap-2">
            <button className="rvk-pill on" onClick={() => navigate('/dashboard/edu-calendar')} data-testid="educator-open-register">Open register</button>
            <button className="rvk-pill" onClick={() => navigate('/dashboard/edu-live')}>Start live class</button>
          </div>
        </Box>
      )}
      <RoleCards testId="educator-quick-actions" items={[
        { img: ROLE_IMG.academy, label: 'Teach', title: 'Training calendar', line: 'Create, publish and run sessions.', cta: 'Open calendar', path: '/dashboard/edu-calendar' },
        { img: ROLE_IMG.team, label: 'Live', title: 'Live class', line: 'Students join with a code.', cta: 'Start a class', path: '/dashboard/edu-live' },
        { img: ROLE_IMG.quotes, label: 'Guides', title: 'Ask a question', line: 'Short answers from the approved guides.', cta: 'Ask now', path: '/dashboard/edu-ask' },
        { img: ROLE_IMG.salons, label: 'Salons', title: 'Requests', line: 'Turn a salon request into a date.', cta: 'See requests', path: '/dashboard/edu-requests' },
      ]} />
      <ProfilePhotoCard />
    </div>
  );
}

export function EducatorCalendar() {
  const hdr = useHdr();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(null);
  const [f, setF] = useState({ brand: 'Revitalise Keratin', course: 'Standard', city: '', venue: '', date: '', time: '10:00', cap: '12' });
  const [reg, setReg] = useState('');
  const load = useCallback(() => axios.get(`${API}/educator/events`, { headers: hdr }).then(r => {
    setEvents(r.data.events);
    setOpen(o => o ? r.data.events.find(e => e.event_id === o.event_id) || null : null);
  }).catch(() => {}), []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const act = async (id, action, body = {}) => {
    try { const r = await axios.post(`${API}/educator/events/${id}/${action}`, body, { headers: hdr }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not do that'); }
  };
  const create = async () => {
    try { await axios.post(`${API}/educator/events`, { ...f, cap: Number(f.cap) }, { headers: hdr }); toast.success('Saved as draft'); setF({ ...f, city: '', venue: '', date: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  if (open) {
    const free = open.cap - open.booked.length;
    return (
      <div className="space-y-6" data-testid="educator-event-detail">
        <button className="rvk-pill" onClick={() => setOpen(null)} data-testid="edu-back-to-calendar">&larr; Back to calendar</button>
        <AcademyHero eyebrow={`${open.brand} · ${open.course}`} title={`${open.city} · ${dfmt(open.date)}`}
          lede={`${open.time} · ${open.venue} · ${open.booked.length} of ${open.cap} booked${open.wait.length ? ` · ${open.wait.length} waiting` : ''}`} />
        <div className="flex gap-2 flex-wrap">
          {open.status === 'draft' && <button className="rvk-pill on" onClick={() => act(open.event_id, 'publish')} data-testid="edu-publish">Publish</button>}
          {open.status === 'published' && <button className="rvk-pill" onClick={() => act(open.event_id, 'complete')}>Mark completed</button>}
          {open.status !== 'cancelled' && <button className="rvk-pill" onClick={() => act(open.event_id, 'cancel')} data-testid="edu-cancel">Cancel session</button>}
          <Tag label={open.status} tone={open.status === 'published' ? 'ok' : open.status === 'cancelled' ? 'bad' : 'new'} />
        </div>
        <Box testId="edu-register">
          <h2 className="text-lg">Register — attendance goes on the Academy record, it does not pass a test</h2>
          {open.booked.map(n => (
            <div key={n} className="flex items-center justify-between border-b border-[#E8DDD5] pb-2">
              <span className="text-sm">{n}</span>
              <span className="flex gap-2 items-center">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-[#B99A65]" checked={!!open.attendance?.[n]}
                    onChange={e => act(open.event_id, 'attendance', { name: n, present: e.target.checked })}
                    data-testid={`edu-att-${n.replace(/\s+/g, '-')}`} />
                  Attended
                </label>
                <button className="text-xs underline text-[#9A3B2E]" onClick={() => act(open.event_id, 'unbook', { name: n })}>Remove</button>
              </span>
            </div>
          ))}
          {open.wait.length > 0 && <p className="text-xs text-[#64525D]">Waiting list: {open.wait.join(', ')} — a freed place is offered to the next person automatically.</p>}
          <div className="flex gap-2">
            <input className="border border-[#E8DDD5] rounded-md px-3 py-2 text-sm flex-1" placeholder="Student name" value={reg} onChange={e => setReg(e.target.value)} data-testid="edu-register-name" />
            <button className="rvk-pill on" onClick={() => { act(open.event_id, 'register', { name: reg }); setReg(''); }} data-testid="edu-register-btn">
              {free > 0 ? 'Book on' : 'Add to waiting list'}
            </button>
          </div>
        </Box>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="educator-calendar">
      <AcademyHero eyebrow="Educator" title="Training calendar" lede="Create, publish and run your sessions." />
      <div className="space-y-3">
        {events.map(e => (
          <Box key={e.event_id} testId={`edu-event-${e.event_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div><b>{e.brand} · {e.course}</b><p className="text-sm text-[#64525D]">{dfmt(e.date)} at {e.time} · {e.venue}, {e.city}</p></div>
              <Tag label={e.status === 'published' ? (e.booked.length >= e.cap ? 'Full' : 'Open') : e.status} tone={e.status === 'published' ? 'ok' : e.status === 'cancelled' ? 'bad' : 'new'} />
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: '#F1DCE2' }}>
              <i className="block h-full" style={{ width: `${Math.min(100, (e.booked.length / e.cap) * 100)}%`, background: '#B99A65' }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#64525D]">{e.booked.length} of {e.cap} booked{e.wait.length ? ` · ${e.wait.length} waiting` : ''}</span>
              <span className="flex gap-2">
                {e.status === 'draft' && <button className="rvk-pill on" style={{ height: 36 }} onClick={() => act(e.event_id, 'publish')}>Publish</button>}
                <button className="rvk-pill" style={{ height: 36 }} onClick={() => setOpen(e)} data-testid={`edu-open-${e.event_id}`}>Open</button>
              </span>
            </div>
          </Box>
        ))}
        {!events.length && <p className="text-sm text-[#64525D]">No sessions yet — create your first one below.</p>}
      </div>
      <Box testId="edu-new-event">
        <h2 className="text-lg">New training event</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[['brand', ['Revitalise Keratin', 'Revitalise Extreme']], ['course', ['Standard', 'Advanced']]].map(([k, opts]) => (
            <label key={k} className="text-xs text-[#64525D] capitalize">{k}
              <select className="w-full border border-[#E8DDD5] rounded-md px-2 py-2 text-sm mt-1" value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} data-testid={`edu-f-${k}`}>
                {opts.map(o => <option key={o}>{o}</option>)}
              </select>
            </label>
          ))}
          {[['city', 'text'], ['venue', 'text'], ['date', 'date'], ['time', 'time'], ['cap', 'number']].map(([k, t]) => (
            <label key={k} className="text-xs text-[#64525D] capitalize">{k === 'cap' ? 'Places' : k}
              <input type={t} className="w-full border border-[#E8DDD5] rounded-md px-2 py-2 text-sm mt-1" value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} data-testid={`edu-f-${k}`} />
            </label>
          ))}
        </div>
        <button className="rvk-pill on self-start" onClick={create} data-testid="edu-create-event">Save as draft</button>
      </Box>
    </div>
  );
}

const LSECT = ['Welcome and safety', 'Product knowledge', 'Step by step method', 'Quick questions', 'Final check and results'];
const LESSONS = ['Standard keratin method', 'Afro and textured hair', 'Thermal Protective Mask', 'Home care and retail'];

export function EducatorLive() {
  const hdr = useHdr();
  const [live, setLive] = useState(null);
  const [lesson, setLesson] = useState(LESSONS[0]);
  const load = useCallback(() => axios.get(`${API}/educator/live`, { headers: hdr }).then(r => setLive(r.data.live)).catch(() => {}), []); // eslint-disable-line
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const post = async (action, body = {}) => {
    try { const r = await axios.post(`${API}/educator/live/${action}`, body, { headers: hdr }); if (r.data.message) toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not do that'); }
  };
  if (!live) return (
    <div className="space-y-6" data-testid="educator-live">
      <AcademyHero eyebrow="Educator" title="Live class" lede="Students join on their own phone with a code. You release each section when you are ready." />
      <Box testId="edu-live-choose">
        <h2 className="text-lg">Choose the lesson</h2>
        {LESSONS.map(l => (
          <button key={l} className="text-left text-sm rounded-lg p-3" style={{ background: lesson === l ? '#F1DCE2' : 'transparent' }}
            onClick={() => setLesson(l)} data-testid={`edu-lesson-${l.replace(/\s+/g, '-')}`}>{lesson === l ? '● ' : '○ '}{l}</button>
        ))}
        <button className="rvk-pill on self-start" onClick={() => post('start', { lesson })} data-testid="edu-live-start">Start the class</button>
      </Box>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="educator-live-on">
      <AcademyHero eyebrow="Live now" title={live.lesson} />
      <div className="rounded-2xl p-8 text-center" style={{ background: '#291525' }} data-testid="edu-live-code">
        <p className="eyebrow" style={{ color: '#D9BE8E' }}>Students join with this code</p>
        <b className="block" style={{ fontSize: 52, letterSpacing: 10, color: '#F8F4F1' }}>{live.code}</b>
        <p style={{ color: '#CDBFC9' }}>or go to the Academy and choose Join a class</p>
        <p style={{ color: '#F8F4F1', fontSize: 18 }}><b>{(live.joined || []).length}</b> students joined</p>
      </div>
      <Box testId="edu-live-sections">
        <h2 className="text-lg">Sections</h2>
        {LSECT.map((s, i) => (
          <div key={s} className="flex items-center justify-between border-b border-[#E8DDD5] pb-2">
            <span className="text-sm">{i + 1}. {s}</span>
            {(live.released || []).includes(i)
              ? <Tag label="Released" tone="ok" />
              : <button className="rvk-pill on" style={{ height: 36 }} onClick={() => post('release', { section: i })} data-testid={`edu-release-${i}`}>Release</button>}
          </div>
        ))}
      </Box>
      {(live.released || []).some(i => i >= 3) && (
        <Box testId="edu-live-answers">
          <h2 className="text-lg">Answers coming in</h2>
          <p className="text-sm text-[#64525D]">{Math.min(live.answers, (live.joined || []).length)} of {(live.joined || []).length} students have answered</p>
        </Box>
      )}
      <button className="rvk-pill" onClick={() => post('end')} data-testid="edu-live-end">End class and save results</button>
    </div>
  );
}

export function EducatorAsk() {
  const hdr = useHdr();
  const [q, setQ] = useState('');
  const [ans, setAns] = useState(null);
  const ask = async (text) => {
    const question = text || q;
    if (!question.trim()) return;
    try { const r = await axios.post(`${API}/educator/ask`, { q: question }, { headers: hdr }); setAns(r.data); setQ(question); }
    catch { toast.error('Could not get the answer'); }
  };
  const speak = () => {
    if (!ans?.answer?.length) return;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(ans.answer.join('. ')));
  };
  return (
    <div className="space-y-6" data-testid="educator-ask">
      <AcademyHero eyebrow="Educator" title="Ask a question" lede="Short answers from the approved Revitalise guides only, ready to read out to the class." />
      <Box testId="edu-ask-box">
        <input className="border border-[#E8DDD5] rounded-md px-3 py-2.5 text-sm" placeholder="For example: does keratin need a strand test?"
          value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && ask()} data-testid="edu-ask-input" />
        <button className="rvk-pill on self-start" onClick={() => ask()} data-testid="edu-ask-btn"><Mic size={14} /> Get the answer</button>
        <div className="flex gap-2 flex-wrap">
          {['Does keratin need a strand test?', 'TPM rules', 'Colour after keratin', 'Standard mask method', 'Boto masks'].map(x => (
            <button key={x} className="rvk-pill" style={{ height: 36 }} onClick={() => ask(x)} data-testid={`edu-ask-chip-${x.split(' ')[0].toLowerCase()}`}>{x}</button>
          ))}
        </div>
      </Box>
      {ans && (
        <div className="rounded-2xl p-6" style={{ background: '#291525' }} data-testid="edu-answer">
          <p className="eyebrow" style={{ color: '#D9BE8E' }}>Answer</p>
          {ans.answer?.length ? (
            <>
              <ul className="list-disc pl-6" style={{ color: '#F8F4F1', fontSize: 18, lineHeight: 1.6 }}>
                {ans.answer.map(a => <li key={a}>{a}</li>)}
              </ul>
              <div className="flex gap-2 mt-4">
                <button className="rvk-pill" onClick={speak} data-testid="edu-answer-listen"><Volume2 size={14} /> Listen</button>
                <button className="rvk-pill" onClick={() => window.speechSynthesis.cancel()}><Square size={14} /> Stop</button>
              </div>
            </>
          ) : <p style={{ color: '#F8F4F1', margin: 0 }}>{ans.message}</p>}
        </div>
      )}
    </div>
  );
}

export function EducatorRequests() {
  const hdr = useHdr();
  const [reqs, setReqs] = useState([]);
  const [dates, setDates] = useState({});
  const load = useCallback(() => axios.get(`${API}/educator/requests`, { headers: hdr }).then(r => setReqs(r.data.requests)).catch(() => {}), []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const act = async (id, action, body = {}) => {
    try { const r = await axios.post(`${API}/educator/requests/${id}/${action}`, body, { headers: hdr }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not do that'); }
  };
  return (
    <div className="space-y-6" data-testid="educator-requests">
      <AcademyHero eyebrow="Educator" title="Training requests" lede="Requests from salons — turn each one into a training date." />
      {reqs.map(r => (
        <Box key={r.request_id} testId={`edu-req-${r.request_id}`}>
          <div className="flex items-center justify-between">
            <div><b>{r.from}</b><p className="text-sm text-[#64525D]">{r.brand} · {r.people} people · {r.when}</p></div>
            <Tag label={r.status} tone={r.status === 'New' ? 'new' : r.status === 'Declined' ? 'bad' : 'ok'} />
          </div>
          <p className="text-sm text-[#64525D]">Interested in: {r.products}</p>
          {r.status === 'New' && (
            <div className="flex gap-2 items-center flex-wrap">
              <input type="date" className="border border-[#E8DDD5] rounded-md px-2 py-2 text-sm"
                value={dates[r.request_id] || ''} onChange={e => setDates({ ...dates, [r.request_id]: e.target.value })} data-testid={`edu-req-date-${r.request_id}`} />
              <button className="rvk-pill on" style={{ height: 38 }} onClick={() => act(r.request_id, 'accept', { date: dates[r.request_id] })} data-testid={`edu-req-accept-${r.request_id}`}>Create a training date</button>
              <button className="rvk-pill" style={{ height: 38 }} onClick={() => act(r.request_id, 'decline')}>Decline</button>
            </div>
          )}
        </Box>
      ))}
      {!reqs.length && <p className="text-sm text-[#64525D]">No requests yet.</p>}
    </div>
  );
}

export function EducatorStudents() {
  const hdr = useHdr();
  const [studs, setStuds] = useState([]);
  useEffect(() => { axios.get(`${API}/educator/students`, { headers: hdr }).then(r => setStuds(r.data.students)).catch(() => {}); }, []); // eslint-disable-line
  return (
    <div className="space-y-6" data-testid="educator-students">
      <AcademyHero eyebrow="Educator" title="My students" lede="Everyone booked on your sessions and how far they have got." />
      {studs.map(s => (
        <Box key={s.name} testId={`edu-stud-${s.name.replace(/\s+/g, '-')}`}>
          <div className="flex items-center justify-between">
            <div><b>{s.name}</b><p className="text-sm text-[#64525D]">{s.where}</p></div>
            <span className="text-sm text-[#64525D]">{dfmt(s.last)}</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: '#F1DCE2' }}>
            <i className="block h-full" style={{ width: `${Math.min(100, (s.lessons_done / s.lessons_total) * 100)}%`, background: '#B99A65' }} />
          </div>
          <p className="text-sm text-[#64525D]">{s.lessons_done} of {s.lessons_total} Standard lessons</p>
        </Box>
      ))}
      {!studs.length && <p className="text-sm text-[#64525D]">No students yet — they appear when booked on your sessions.</p>}
    </div>
  );
}
